import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import css from './MatchingRateSection.css';
import {
    IconSpinner,
    NamedLink,
    SecondaryButton,
    MatchingDialog,
    ChangeUserTypeModal,
} from '../../components';
import config from '../../config';
import { IconMatchL } from '../../icons';
import { SCORE_SUFFICIENT, SCORE_TOTAL, SCORE_INSUFFICIENT } from '../../util/user';
import { compose } from 'redux';
import { ensureListing } from '../../util/data';
import DateRepresentation from './DateRepresentation';

const { userTypeRider, userTypeHorseowner } = config;

const MatchingRateSection = ({
    user,
    currentUser,
    isLoggedOut,
    isRiderPage,
    isHorsePage,
    isSameUser,
    /**
     * listing that belongs to the current page
     */
    currentPageListing,
    /**
     * listing that belongs to the current page viewer
     */
    viewerListing,
    scoreReqInProgress,
    scoreData,
    dateFormatConfig,
    rootClassName,
    profileSettingsDescId,
    signUpLinkDescId,
    intl,
    signUpLinkAttributes,
    showDateRepresentation = true,
    ...rest
}) => {
    const [changeUserModalOpen, setChangeUserTypeModalOpen] = useState(false);
    const [matchingModalOpen, setMatchingModalOpen] = useState(false);

    const ensuredListing = ensureListing(currentPageListing);

    const {
        attributes: {
            profile: {
                publicData: { userType },
            },
        },
    } = user;

    const {
        attributes: {
            profile: {
                publicData: { userType: currentUserType },
            },
        },
    } = currentUser;

    const classes = classNames([css.infoBlock, css.root], {
        [rootClassName]: !!rootClassName,
    });

    const signUpLink = (
        <NamedLink
            name="SignupHorseowner"
            to={
                currentPageListing && currentPageListing.id
                    ? {
                          search: `listingId=${currentPageListing.id.uuid}`,
                      }
                    : {}
            }
            {...signUpLinkAttributes}
        >
            <FormattedMessage id="ProfileSettingsForm.signUpLink" />
        </NamedLink>
    );

    const profileSettingsLink = (
        <div className={css.link} onClick={() => setChangeUserTypeModalOpen(true)}>
            <FormattedMessage id="ProfileSettingsForm.profileSettingsLink" />
        </div>
    );

    // noUserAvailableContent - isLoggedOut
    const actionSectionId = isLoggedOut
        ? signUpLinkDescId || 'ProfileSettingsForm.signUpLinkDesc'
        : profileSettingsDescId || 'ProfileSettingsForm.profileSettingsDesc';

    const actionSectionLink = isLoggedOut ? signUpLink : profileSettingsLink;

    const isHorseowner = userType === userTypeHorseowner;
    const isRider = userType === userTypeRider;

    const sameUserTypeReview =
        (!isSameUser && isHorseowner && currentUserType === userTypeHorseowner) ||
        (!isSameUser && isRider && currentUserType === userTypeRider);

    const matchingDialogDataMissed = !currentPageListing || !viewerListing || !scoreData;

    if (scoreReqInProgress)
        return (
            <div className={classes}>
                <IconSpinner />
            </div>
        );

    return (
        <>
            <div className={classes}>
                {showDateRepresentation && (
                    <DateRepresentation
                        currentPageListing={ensuredListing}
                        dateFormatConfig={dateFormatConfig}
                        isHorsePage={isHorsePage}
                        user={user}
                    />
                )}

                {!scoreData && (isHorsePage || isRiderPage) && (isLoggedOut || sameUserTypeReview) && (
                    <p className={css.actionSection}>
                        <FormattedMessage
                            id={actionSectionId}
                            values={{ link: actionSectionLink }}
                        />
                    </p>
                )}
                {scoreData && scoreData.finalScore && (
                    <>
                        <p className={css.matchingScore}>
                            {scoreData.finalScore}% <FormattedMessage id="ListingCard.finalScore" />
                        </p>

                        <code
                            className={classNames({
                                [css.progressBar]: true,
                                [css.matchingInsufficient]:
                                    scoreData.scorePalette === SCORE_INSUFFICIENT,
                                [css.matchingSufficient]:
                                    scoreData.scorePalette === SCORE_SUFFICIENT,
                                [css.matchingTotal]: scoreData.scorePalette === SCORE_TOTAL,
                            })}
                        >
                            <mark style={{ width: `${scoreData.finalScore || 0}%` }} />
                        </code>
                        {!matchingDialogDataMissed && (
                            <SecondaryButton
                                className={css.matchingDialogActionBtn}
                                onClick={() => setMatchingModalOpen(true)}
                            >
                                <IconMatchL />
                                <span>Matching ansehen</span>
                            </SecondaryButton>
                        )}
                        <NamedLink name="MatchingPage" openInNewTab>
                            <FormattedMessage id="ProfileSettingsForm.toMatchingPageLink" />
                        </NamedLink>
                    </>
                )}
            </div>
            {changeUserModalOpen && (
                <ChangeUserTypeModal
                    modalOpen
                    setModalOpen={setChangeUserTypeModalOpen}
                    {...rest}
                />
            )}
            {matchingModalOpen && (
                <MatchingDialog
                    modalOpen
                    onClose={() => setMatchingModalOpen(false)}
                    currentPageListing={currentPageListing}
                    viewerListing={viewerListing}
                    scoreData={scoreData}
                />
            )}
        </>
    );
};

export default compose(injectIntl)(MatchingRateSection);
